import { useAuth } from "@lib/api-hooks";
import React, { useCallback, useEffect, useState } from "react";
import { useFeatureFlag } from "@lib/hooks/useFeatureFlags";
import type { Integration } from "./CombinedIntegrationsDashboard";
import {
  BuildingOfficeIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { Button } from "@tracify/ui/button";
import { Input } from "@tracify/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tracify/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@tracify/ui/dialog";
import { useRequestCreateSiteref } from "@lib/api-hooks/useSiteRefs";
import {
  connectedAdAccountsQueryKey,
  fetchConnectedAdAccounts,
} from "@lib/api-hooks/useAdAccounts/useConnectedAdAccounts";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardHeader } from "@tracify/ui/card";
import { usePostHog } from "posthog-js/react";
import {
  captureAdAccountConnected,
  captureAdAccountConnectionFailed,
  captureAdAccountConnectionUpdated,
  clearPendingAdAccountConnection,
  rememberPendingAdAccountConnection,
} from "@/lib/ad-account-connection-analytics";
import { cn } from "@/lib/utils";

type Props = {
  onClose: (success?: boolean) => void;
  open: boolean;
  integration: Integration;
  isReconnect?: boolean;
  accountId?: string;
  type:
    | "google"
    | "fb"
    | "tiktok"
    | "pinterest"
    | "outbrain"
    | "msads"
    | "taboola";
};

const InitializeAdAccountFlowModal = ({
  onClose,
  open,
  integration,
  type,
  isReconnect,
  accountId,
}: Props) => {
  const posthog = usePostHog();
  const { data: auth } = useAuth();
  const showHeaderOverride = useFeatureFlag("outbrain_header_override");
  const { selectedOrganisationData, stores } = useSelectedStores();
  const customerSiteIds = selectedOrganisationData?.customerSites?.map(
    (el) => el.id
  );

  const organisationStores = stores?.filter((el) =>
    customerSiteIds?.includes(el.csid)
  );
  const requestSiterefCreateMutation = useRequestCreateSiteref();
  const queryClient = useQueryClient();
  const accountChannel = type === "fb" ? "facebook" : type;

  /**
   * Account IDs currently connected for this channel. Used as the analytics
   * baseline so that, once the provider reports which accounts it authorized,
   * we can tell newly connected accounts from re-authorized ones. Returns
   * `null` when the list cannot be determined — analytics must never block
   * the connection flow, and an unknown baseline must not be treated as
   * empty (that would mis-report every existing account as new).
   */
  const loadConnectedAccountIds = useCallback(
    async (fresh: boolean): Promise<string[] | null> => {
      const organisationId = selectedOrganisationData?.id;
      if (!organisationId) return null;
      const queryOptions = {
        queryKey: connectedAdAccountsQueryKey({
          organisationId,
          includeAll: true,
        }),
        queryFn: () =>
          fetchConnectedAdAccounts({ organisationId, includeAll: true }),
      };
      try {
        const data = fresh
          ? await queryClient.fetchQuery({ ...queryOptions, staleTime: 0 })
          : await queryClient.ensureQueryData(queryOptions);
        return (
          data.accounts?.[accountChannel]?.map(
            (account) => account.account_id
          ) ?? []
        );
      } catch {
        return null;
      }
    },
    [accountChannel, queryClient, selectedOrganisationData?.id]
  );

  const [storeErrorMessage, setStoreErrorMessage] = useState("");
  const [outbrainFeatureFlagOverride, setOutbrainFeatureFlagOverride] =
    useState("");
  const [outbrainCredentials, setOutbrainCredentials] = useState({
    email: "",
    password: "",
  });
  const [taboolaCredentials, setTaboolaCredentials] = useState({
    client_id: "",
    client_secret: "",
  });
  const [currentStep, setCurrentStep] = useState<
    | "intro"
    | "msads-warning"
    | "outbrain-token"
    | "outbrain-credentials"
    | "taboola-credentials"
  >("intro");

  const resetState = useCallback(() => {
    setCurrentStep("intro");
    setStoreErrorMessage("");
    setOutbrainFeatureFlagOverride("");
    setOutbrainCredentials({
      email: "",
      password: "",
    });
    setTaboolaCredentials({
      client_id: "",
      client_secret: "",
    });
  }, [
    setOutbrainCredentials,
    setOutbrainFeatureFlagOverride,
    setTaboolaCredentials,
  ]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  useEffect(() => {
    resetState();
  }, [type, resetState]);

  const isMsads = type === "msads";
  const isOutbrain = type === "outbrain";
  const isTaboola = type === "taboola";
  const hasExtraSteps = isMsads || isOutbrain || isTaboola;
  const isCredentialStep =
    currentStep === "outbrain-credentials" ||
    currentStep === "taboola-credentials";
  const isConnectActionStep =
    (!hasExtraSteps && currentStep === "intro") ||
    currentStep === "msads-warning" ||
    currentStep === "outbrain-credentials" ||
    currentStep === "taboola-credentials";

  /**
   * Credential-based flows (Outbrain/Taboola) resolve in-page, so we can emit
   * the outcome right here: a reconnect re-authorizes the given account, a new
   * connect adds whichever accounts now exist beyond the pre-connect baseline.
   */
  const captureCredentialConnectionOutcome = useCallback(
    async (
      reauthorizationAccountId: string | undefined,
      existingAccountIds: string[] | null
    ) => {
      if (reauthorizationAccountId) {
        captureAdAccountConnectionUpdated(
          posthog,
          type,
          reauthorizationAccountId,
          "reauthorized"
        );
        return;
      }
      if (!existingAccountIds) return;
      const currentAccountIds = await loadConnectedAccountIds(true);
      if (!currentAccountIds) return;
      const existing = new Set(existingAccountIds);
      currentAccountIds
        .filter((accountId) => !existing.has(accountId))
        .forEach((accountId) =>
          captureAdAccountConnected(posthog, type, accountId)
        );
    },
    [loadConnectedAccountIds, posthog, type]
  );

  const onConnectAccount = useCallback(async () => {
    if (integration.disabled) return;

    setStoreErrorMessage("");
    const reauthorizationAccountId = isReconnect ? accountId : undefined;
    clearPendingAdAccountConnection();

    if (!selectedOrganisationData) {
      setStoreErrorMessage(
        "You must select an organisation to connect an ad account."
      );
    } else {
      try {
        if (type === "outbrain") {
          if (!outbrainCredentials.email || !outbrainCredentials.password) {
            setStoreErrorMessage("Please provide both email and password");
            return;
          }

          const existingAccountIds = await loadConnectedAccountIds(false);
          await requestSiterefCreateMutation.mutateAsync({
            organisationId: selectedOrganisationData?.id,
            type: type,
            credentials: outbrainCredentials,
            ...(outbrainFeatureFlagOverride
              ? { featureFlagOverride: outbrainFeatureFlagOverride }
              : {}),
          });
          setOutbrainCredentials({
            email: "",
            password: "",
          });
          await captureCredentialConnectionOutcome(
            reauthorizationAccountId,
            existingAccountIds
          );
          onClose(true);
        } else if (type === "taboola") {
          if (
            !taboolaCredentials.client_id ||
            !taboolaCredentials.client_secret
          ) {
            setStoreErrorMessage(
              "Please provide both Client ID and Client Secret"
            );
            return;
          }

          const existingAccountIds = await loadConnectedAccountIds(false);
          await requestSiterefCreateMutation.mutateAsync({
            organisationId: selectedOrganisationData?.id,
            type: type,
            credentials: taboolaCredentials,
          });
          setTaboolaCredentials({
            client_id: "",
            client_secret: "",
          });
          await captureCredentialConnectionOutcome(
            reauthorizationAccountId,
            existingAccountIds
          );
          onClose(true);
        } else {
          // OAuth: the provider redirects back to /success?account_ids=…
          // Persist the baseline so the success page can tell connected from
          // re-authorized accounts.
          const existingAccountIds = await loadConnectedAccountIds(false);
          if (existingAccountIds) {
            rememberPendingAdAccountConnection(type, existingAccountIds);
          }
          await requestSiterefCreateMutation.mutateAsync({
            organisationId: selectedOrganisationData?.id,
            type: type,
          });
        }
      } catch (error: any) {
        clearPendingAdAccountConnection();
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred!";
        captureAdAccountConnectionFailed(posthog, type, errorMessage, {
          accountId: reauthorizationAccountId,
        });
        setStoreErrorMessage(errorMessage);
        return;
      }
    }
  }, [
    integration.disabled,
    requestSiterefCreateMutation,
    selectedOrganisationData,
    type,
    outbrainCredentials,
    outbrainFeatureFlagOverride,
    taboolaCredentials,
    onClose,
    posthog,
    isReconnect,
    accountId,
    setOutbrainCredentials,
    setTaboolaCredentials,
    loadConnectedAccountIds,
    captureCredentialConnectionOutcome,
  ]);

  const handleContinue = useCallback(() => {
    if (integration.disabled) return;

    if (!selectedOrganisationData) {
      setStoreErrorMessage(
        "You must select an organisation to connect an ad account."
      );
      return;
    }

    setStoreErrorMessage("");
    if (isMsads) {
      setCurrentStep("msads-warning");
      return;
    }

    if (isOutbrain) {
      setCurrentStep("outbrain-token");
      return;
    }

    if (isTaboola) {
      setCurrentStep("taboola-credentials");
    }
  }, [
    integration.disabled,
    isMsads,
    isOutbrain,
    isTaboola,
    selectedOrganisationData,
  ]);

  const handleBack = useCallback(() => {
    setStoreErrorMessage("");

    switch (currentStep) {
      case "msads-warning":
      case "outbrain-token":
      case "taboola-credentials":
        setCurrentStep("intro");
        return;
      case "outbrain-credentials":
        setCurrentStep("outbrain-token");
        return;
      default:
        return;
    }
  }, [currentStep]);

  const handleOutbrainTokenConfirm = useCallback(() => {
    setStoreErrorMessage("");
    setCurrentStep("outbrain-credentials");
  }, []);

  const content = (
    <div className="flex-1 space-y-4">
      <p>
        This connection will be available to your entire organization. After
        connecting, you&apos;ll assign each ad account to the stores where you
        want to use its data.
      </p>
      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-base text-foreground-soft font-semibold">
            Organisation
          </p>
          <Card>
            <CardHeader>
              <p className="flex items-center">
                <BuildingOfficeIcon className="h-5 w-5 mr-1 text-foreground-soft flex-none" />
                {selectedOrganisationData?.name}
              </p>
            </CardHeader>
          </Card>
        </div>
        <div className="space-y-2">
          <p className="text-base text-foreground-soft font-semibold">
            Stores in this organisation
          </p>
          <Card>
            <CardHeader>
              <ul className="list-disc list-inside gap-1">
                {organisationStores?.map((el) => (
                  <li key={el.csid}>{el.description}</li>
                ))}
              </ul>
            </CardHeader>
          </Card>
        </div>
      </div>
      {storeErrorMessage && !isCredentialStep && (
        <p className="mt-4 font-semibold text-red-500">{storeErrorMessage}</p>
      )}
    </div>
  );

  const msadsWarningContent = (
    <div className="flex-1 space-y-6">
      <p>
        Some organizations restrict which apps employees can connect to their
        Microsoft account. If you see a message like &quot;Administrator
        approval required&quot; or &quot;Need admin approval&quot; when
        connecting, don&apos;t worry - this is a security setting controlled by
        your IT team, not a problem with your account.
      </p>
      <div className="space-y-2">
        <p className="font-semibold">If this happens to you</p>
        <Card>
          <CardHeader className="space-y-2">
            <p>
              You&apos;ll need someone from your IT team to approve Tracify.
              Look for a person with one of these roles:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>Microsoft 365 Administrator</li>
              <li>Global Administrator</li>
              <li>Application Administrator</li>
            </ul>
          </CardHeader>
        </Card>
      </div>
      <p>
        Not sure who that is? Try your IT helpdesk or whoever manages your
        company&apos;s Microsoft accounts.{` `}
        <a
          href="https://knowledge.tracify.ai/en/articles/588262-troubleshooting-microsoft-ads-connections-issues"
          target="_blank"
          rel="noreferrer"
          className="font-semibold hover:underline"
        >
          Learn more here
        </a>
      </p>
      {storeErrorMessage && (
        <p className="mt-4 font-semibold text-red-500">{storeErrorMessage}</p>
      )}
    </div>
  );

  const outbrainTokenContent = (
    <div className="flex-1 space-y-6">
      <p className="text-sm">Before we begin, please note:</p>
      <Card className="bg-primary-300/15 border border-primary-300/70 text-sm space-y-4">
        <CardHeader>
          <div className="flex items-center gap-2">
            <InformationCircleIcon className="h-6 w-6 text-foreground-soft flex-none" />
            <div>
              <p className="text-sm">
                Your Outbrain account must have access to the Amplify API. You
                can request access from Outbrain partner support here:{" "}
                <a
                  href="https://www.outbrain.com/partner-api/"
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  https://www.outbrain.com/partner-api/
                </a>
              </p>
            </div>
          </div>
        </CardHeader>
      </Card>
      {storeErrorMessage && (
        <p className="mt-4 font-semibold text-red-500">{storeErrorMessage}</p>
      )}
    </div>
  );

  const outbrainCredentialsContent = (
    <div className="space-y-6">
      <div>
        <label htmlFor="email" className="block text-sm font-medium mb-1">
          Outbrain Email Address
        </label>
        <Input
          id="email"
          type="email"
          value={outbrainCredentials.email}
          onChange={(e) =>
            setOutbrainCredentials((prev) => ({
              ...prev,
              email: e.target.value,
            }))
          }
          placeholder="Your Outbrain email address"
          className="w-full"
        />
      </div>
      <div>
        <label htmlFor="password" className="block text-sm font-medium mb-1">
          Outbrain Password
        </label>
        <Input
          id="password"
          type="password"
          value={outbrainCredentials.password}
          onChange={(e) =>
            setOutbrainCredentials((prev) => ({
              ...prev,
              password: e.target.value,
            }))
          }
          placeholder="Your Outbrain password"
          className="w-full"
        />
      </div>
      {showHeaderOverride && (
        <div>
          <label className="block text-sm font-medium mb-1">
            Feature Flag Override (testing only)
          </label>
          <Select
            value={outbrainFeatureFlagOverride}
            onValueChange={setOutbrainFeatureFlagOverride}
          >
            <SelectTrigger
              className="w-full"
              aria-label="Feature Flag Override (testing only)"
            >
              <SelectValue placeholder="Select a mock flag..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MOCK_OUTBRAIN_API_AUTHENTICATE_200">
                MOCK_OUTBRAIN_API_AUTHENTICATE_200
              </SelectItem>
              <SelectItem value="MOCK_OUTBRAIN_API_AUTHENTICATE_401">
                MOCK_OUTBRAIN_API_AUTHENTICATE_401
              </SelectItem>
              <SelectItem value="MOCK_OUTBRAIN_API_AUTHENTICATE_500">
                MOCK_OUTBRAIN_API_AUTHENTICATE_500
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <p className="text-sm">
        These permissions allow us to show your ad insights.{" "}
        <a
          href="https://knowledge.tracify.ai/en/articles/88291-how-do-i-set-up-outbrain"
          target="_blank"
          rel="noreferrer"
          className="underline"
        >
          Learn more
        </a>
      </p>
      {storeErrorMessage && (
        <p className="mt-4 font-medium text-sm text-red-500">
          {storeErrorMessage}
        </p>
      )}
    </div>
  );

  const taboolaCredentialsContent = (
    <div className="space-y-6">
      <div>
        <label htmlFor="client_id" className="block text-sm font-medium mb-1">
          Taboola Client ID
        </label>
        <Input
          id="client_id"
          type="text"
          value={taboolaCredentials.client_id}
          onChange={(e) =>
            setTaboolaCredentials((prev) => ({
              ...prev,
              client_id: e.target.value,
            }))
          }
          placeholder="Your Taboola Client ID"
          className="w-full"
        />
      </div>
      <div>
        <label
          htmlFor="client_secret"
          className="block text-sm font-medium mb-1"
        >
          Taboola Client Secret
        </label>
        <Input
          id="client_secret"
          type="password"
          value={taboolaCredentials.client_secret}
          onChange={(e) =>
            setTaboolaCredentials((prev) => ({
              ...prev,
              client_secret: e.target.value,
            }))
          }
          placeholder="Your Taboola Client Secret"
          className="w-full"
        />
      </div>
      <p className="text-sm">
        These permissions allow us to show your ad insights.{" "}
        <a
          href="https://knowledge.tracify.ai/en/articles/88292-how-do-i-set-up-taboola"
          target="_blank"
          rel="noreferrer"
          className="underline"
        >
          Learn more
        </a>
      </p>
      {storeErrorMessage && (
        <p className="mt-4 font-medium text-sm text-red-500">
          {storeErrorMessage}
        </p>
      )}
    </div>
  );

  const noteContent = (
    <ul className="list-disc list-inside space-y-1">
      <li>
        Your {integration.name} account must have access to each ad account you
        want to connect
      </li>
      <li>
        When {integration.name} asks for permissions, approve all of them so
        Tracify can display your ad insights
      </li>
      {type === "fb" && (
        <li>
          If you manage multiple Meta Business Portfolios, each one requires a
          separate connection.
        </li>
      )}
    </ul>
  );

  const title = `${isReconnect ? "Reconnect" : "Connect"} a ${
    integration.name
  } Account`;
  const isIntegrationDisabled =
    !!auth?.payload?.ema && auth?.payload?.ema?.endsWith("@tracify.ai")
      ? false
      : integration.disabled;

  const dialogTitle =
    currentStep === "msads-warning"
      ? "One more thing before you connect..."
      : currentStep === "outbrain-token"
        ? "Confirm Amplify API Access"
        : currentStep === "outbrain-credentials"
          ? "Connect with your Outbrain credentials"
          : currentStep === "taboola-credentials"
            ? "Connect with your Taboola credentials"
            : title;

  const primaryButtonLabel =
    currentStep === "intro"
      ? hasExtraSteps
        ? "Continue"
        : integration.buttonText
      : currentStep === "msads-warning"
        ? "Connect Microsoft Ad Account"
        : currentStep === "outbrain-token"
          ? "I confirm my account has access"
          : currentStep === "outbrain-credentials"
            ? "Connect Outbrain Account"
            : "Connect Taboola Account";

  const handlePrimaryAction = () => {
    if (currentStep === "intro") {
      if (hasExtraSteps) {
        handleContinue();
      } else {
        void onConnectAccount();
      }
      return;
    }

    if (currentStep === "outbrain-token") {
      handleOutbrainTokenConfirm();
      return;
    }

    void onConnectAccount();
  };

  const hasBackButton = currentStep !== "intro";
  const shouldShowIntroNote = currentStep === "intro";
  const stepContent =
    currentStep === "msads-warning"
      ? msadsWarningContent
      : currentStep === "outbrain-token"
        ? outbrainTokenContent
        : currentStep === "outbrain-credentials"
          ? outbrainCredentialsContent
          : currentStep === "taboola-credentials"
            ? taboolaCredentialsContent
            : content;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {stepContent}
          {shouldShowIntroNote && (
            <Card className="bg-primary-300/15 border border-primary-300/70 text-sm space-y-4">
              <CardHeader>
                <p className="font-semibold">Before you connect</p>
                {noteContent}
              </CardHeader>
            </Card>
          )}

          <div
            className={cn("flex", {
              "justify-between": hasBackButton,
              "justify-end": !hasBackButton,
              "mt-6": hasExtraSteps,
              "mt-4": !hasExtraSteps,
            })}
          >
            {hasBackButton && (
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
            )}
            <Button
              variant="primary"
              disabled={isIntegrationDisabled}
              loading={
                isConnectActionStep && requestSiterefCreateMutation.isPending
              }
              onClick={handlePrimaryAction}
            >
              {primaryButtonLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InitializeAdAccountFlowModal;
